/**
 * AtlasGroundWarModule — PLAYER-DECLARED GROUND WARS, the client (2026-09-24; research §5.5 "a wardec row gating
 * strikePlayer between members"). Server rules in lib/ground_war_ops.ts + reducers/ground_war.ts; the PvP mirror of
 * who is lawful to strike is lib/pvp_context.ts (`warTargets`).
 *
 *   THE TOWN TAB "Wars": every war being fought (yours first) — the sides, the score, the stakes and the clock; the
 *     leader's actions (declare with a stake, answer a declaration by matching it, propose peace, surrender) and the
 *     war's feed. A war is between two CITIES, CIVILISATIONS, GUILDS or GROUPS — a player civilisation against another,
 *     a city against a city, a guild against a guild — declared by the side's leader (the mayor, the civilisation's
 *     founding mayor, the guild leader, a group's leader).
 *   AT WAR: a chip under the wallet (your war, the score, the time left); a red marker over every member of the other
 *     side you can see (they may strike you, you may strike them — anywhere in the Commons); and at an enemy SEAT (a
 *     City Hall, the guild hall) E plants your side's banner — hold the ground 60 s uncontested for BANNER_POINTS.
 * Levers: `__groundWar()`.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import type { Identity } from 'spacetimedb';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { Phase, makeSystem } from '../engine-compat.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { interactFocus } from '../lib/interact_focus.js';
import { onInteract, interactPrompt } from '../lib/interact_input.js';
import { myWars, warTargets, sideInWar, type GroundWarRow } from '../lib/pvp_context.js';
import { el, btn, input, select, head, chip, muted, card, act, debounced, ms, field } from './town/town_ui.js';
import { registerTownTab } from './town/town_tabs.js';
import { softRender, fieldByKey } from '../lib/soft_render.js';
import { footprintOf } from '@shared/types/structures.js';
import { distanceToBox } from '../lib/guild_rules.js';
import { tableStamp } from '../lib/fast_find.js';

/** A seat building; the distance is to its WALLS (guild_rules.distanceToBox — the server's rule). */
interface WarSeat { x: number; z: number; hx: number; hz: number; yaw: number }
const seatDistance = (px: number, pz: number, s: WarSeat): number => distanceToBox(px, pz, s);

const WAR_FEE: Record<string, number> = { city: 1000, civ: 2500, guild: 1000, group: 1000 };
const BANNER_R_M = 20;
const BANNER_HOLD_S = 60;
const BANNER_GAP_S = 30 * 60;
const MARK_RANGE_M = 160;
const KIND_LABEL: Record<string, string> = { city: 'City', civ: 'Civilisation', guild: 'Guild', group: 'Group' };
/** The tables myWars() reads (pvp_context.memberOfEntity) — the memo's key. */
const WAR_TABLES = ['groundWar', 'city', 'citizen', 'playerCivMember', 'guildMember', 'groupMember'] as const;
/** A stake box's value as whole credits (2026-09-25): `BigInt(Math.floor(Number('Infinity')))` threw a RangeError out
 *  of the click handler on "1e999" / "Infinity"; a non-finite or negative entry is 0, a huge one capped. */
function stakeOf(text: string): bigint {
  const n = Number(text);
  return Number.isFinite(n) && n > 0 ? BigInt(Math.floor(Math.min(n, Number.MAX_SAFE_INTEGER))) : 0n;
}
/** The tables a war's seats come from. */
const SEAT_TABLES = ['structure', 'city', 'playerCivMember', 'guild'] as const;

interface EventRow { eventId: bigint; warId: bigint; kind: string; text: string; at: { microsSinceUnixEpoch: bigint } }
interface CityRow { cityId: bigint; name: string; mayor: Identity; cityHallStructureId: bigint; centerX: number; centerZ: number; status: string }
interface CivRow { civId: bigint; name: string; founderCityId: bigint; status: string }
interface CivMemberRow { civId: bigint; cityId: bigint }
interface GuildRow { guildId: bigint; name: string; tag: string; founder: Identity; leaderId: Identity; hallStructureId: bigint }
interface GroupRow { groupId: bigint; name: string; state: string }
interface GroupMemberRow { groupId: bigint; identity: Identity; role: string }
interface StructureRow { structureId: bigint; posX: number; posZ: number; yaw: number; structureType: string }

interface Entity { kind: string; id: bigint; name: string }

export class AtlasGroundWarModule implements IModule {
  readonly name = 'AtlasGroundWar';
  private app!: App;
  private scene!: THREE.Scene;
  private subbed = false;
  private tabEl: HTMLElement | null = null;
  private chipEl: HTMLDivElement | null = null;
  private prompt: HTMLDivElement | null = null;
  private marks = new Map<string, THREE.Sprite>();
  private markMat: THREE.SpriteMaterial | null = null;
  private focused: { id: string; warId: bigint } | null = null;
  private planting: { warId: bigint; x: number; z: number; seat: WarSeat | null; startedMs: number; sending: boolean } | null = null;
  private bannerMesh: THREE.Group | null = null;
  private scanAt = 0;
  private warsMemo: { key: string; me: string; at: number; v: Array<{ war: GroundWarRow; side: 'a' | 'b' }> } | null = null;
  private readonly seatMemo = new Map<string, { key: string; at: number; v: WarSeat[] }>();
  /** F2: the bound Interact key AND the pad's (`atlas:place-requested`), one press — lib/interact_input.ts. */
  private offInteract: (() => void) | null = null;
  private readonly rerender = debounced(() => { if (this.tabEl?.isConnected) this.renderTab(this.tabEl); }, 300);

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    registerTownTab({ key: 'wars', label: 'Wars', order: 0.5, render: (h) => { this.tabEl = h; this.renderTab(h); } });
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    app.world.addSystem(makeSystem('GroundWar', Phase.UI, 35, () => this.tick()));
    this.offInteract = onInteract(() => this.key());
    const c = document.createElement('div');
    c.id = 'ground-war-chip';
    c.className = 'ax-chip';
    c.style.cssText = 'position:fixed;top:122px;left:50%;transform:translateX(-50%);z-index:31;display:none;pointer-events:none;'
      + 'font:12px var(--ax-font);color:var(--ax-bad);border-color:color-mix(in srgb, var(--ax-bad) 45%, var(--ax-line2));background:color-mix(in srgb, var(--ax-bg) 80%, transparent);padding:4px 10px';
    document.body.appendChild(c);
    this.chipEl = c;
    const p = document.createElement('div');
    p.id = 'ground-war-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:262px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-bad) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    (window as unknown as { __groundWar?: unknown }).__groundWar = () => ({
      wars: this.rows<GroundWarRow>('groundWar').map((w) => ({ id: String(w.warId), a: w.attackerName, b: w.defenderName, state: w.state, score: [w.scoreA, w.scoreB] })),
      mine: myWars().map(({ war, side }) => ({ id: String(war.warId), side })),
      enemiesSeen: [...warTargets()],
      marks: this.marks.size,
      planting: this.planting ? { warId: String(this.planting.warId), heldS: Math.round((performance.now() - this.planting.startedMs) / 1000) } : null,
      leads: this.myEntities().map((e) => `${e.kind}:${e.id}`),
    });
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    const db = conn.db as unknown as Record<string, { onInsert?(cb: () => void): void; onUpdate?(cb: () => void): void; onDelete?(cb: () => void): void } | undefined>;
    for (const n of ['groundWar', 'groundWarEvent']) {
      const t = db[n];
      t?.onInsert?.(() => this.rerender()); t?.onUpdate?.(() => this.rerender()); t?.onDelete?.(() => this.rerender());
    }
    try {
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM ground_war', 'SELECT * FROM ground_war_event',
        'SELECT * FROM city', 'SELECT * FROM citizen', 'SELECT * FROM player_civ', 'SELECT * FROM player_civ_member',
        'SELECT * FROM guild', 'SELECT * FROM guild_member', 'SELECT * FROM "group"', 'SELECT * FROM group_member',
      ]);
    } catch { /* pre-publish */ }
  }

  private rows<T>(name: string): T[] {
    try { const t = (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<T> } | undefined>)?.[name]; return t ? [...t.iter()] : []; }
    catch { return []; }
  }
  private me(): string | null { return atlas.identityHex; }

  /** The sides I may lead (declare, answer, make peace for). The mirror of ground_war_ops.mayLeadEntity. */
  private myEntities(): Entity[] {
    const me = this.me();
    if (!me) return [];
    const out: Entity[] = [];
    const cities = this.rows<CityRow>('city');
    for (const c of cities) if (c.mayor.toHexString() === me && c.status !== 'invalid') out.push({ kind: 'city', id: c.cityId, name: c.name });
    for (const v of this.rows<CivRow>('playerCiv')) {
      if (v.status !== 'formed') continue;
      const f = cities.find((c) => c.cityId === v.founderCityId);
      if (f && f.mayor.toHexString() === me) out.push({ kind: 'civ', id: v.civId, name: v.name });
    }
    const zero = '0'.repeat(64);
    // The mirror of the server's guildLeadOf (2026-09-25): the master while a MEMBER, else the founder while a member,
    // else nobody — a founder who had left was still offered declare / peace / surrender the server refuses.
    const members = new Set<string>();
    for (const m of this.rows<{ guildId: bigint; identity: Identity }>('guildMember')) members.add(`${m.guildId}:${m.identity.toHexString()}`);
    for (const g of this.rows<GuildRow>('guild')) {
      const master = g.leaderId.toHexString().replace(/^0x/, '') === zero ? null : g.leaderId.toHexString();
      const lead = master !== null && members.has(`${g.guildId}:${master}`) ? master
        : members.has(`${g.guildId}:${g.founder.toHexString()}`) ? g.founder.toHexString() : null;
      if (lead === me) out.push({ kind: 'guild', id: g.guildId, name: `${g.name}${g.tag ? ` [${g.tag}]` : ''}` });
    }
    for (const m of this.rows<GroupMemberRow>('groupMember')) {
      if (m.identity.toHexString() !== me || m.role !== 'leader') continue;
      const g = this.rows<GroupRow>('group').find((x) => x.groupId === m.groupId);
      if (g) out.push({ kind: 'group', id: g.groupId, name: g.name });
    }
    return out;
  }

  /** Every side a war can be declared on (names from their rows). */
  private allEntities(kind: string): Entity[] {
    if (kind === 'city') return this.rows<CityRow>('city').filter((c) => c.status !== 'invalid').map((c) => ({ kind, id: c.cityId, name: c.name }));
    if (kind === 'civ') return this.rows<CivRow>('playerCiv').filter((v) => v.status === 'formed').map((v) => ({ kind, id: v.civId, name: v.name }));
    if (kind === 'guild') return this.rows<GuildRow>('guild').map((g) => ({ kind, id: g.guildId, name: `${g.name}${g.tag ? ` [${g.tag}]` : ''}` }));
    return this.rows<GroupRow>('group').filter((g) => g.state === 'active').map((g) => ({ kind, id: g.groupId, name: g.name }));
  }

  private leads(kind: string, id: bigint): boolean { return this.myEntities().some((e) => e.kind === kind && e.id === id); }

  /** The tables' mutation stamps as one key, or null when a table is not fast-find patched (NaN never matches). */
  private stampKey(names: readonly string[]): string | null {
    const db = atlas.conn?.db as unknown as Record<string, unknown> | undefined;
    let k = '';
    for (const n of names) { const s = tableStamp(db?.[n]); if (Number.isNaN(s)) return null; k += `${s},`; }
    return k;
  }

  /** myWars(), memoised on the membership tables' stamps (2026-09-25): it materialised city / citizen / civ / guild /
   *  group members with a hex per row, twice per war, EVERY frame for every client while any war was active (3 days).
   *  Recomputed at least once a second anyway — a war ends by the clock (`endsAt`) with no row change. */
  private wars(now: number): Array<{ war: GroundWarRow; side: 'a' | 'b' }> {
    const key = this.stampKey(WAR_TABLES);
    const me = atlas.identityHex ?? '';
    const m = this.warsMemo;
    if (m && m.me === me && (key !== null ? m.key === key && now - m.at < 1000 : now - m.at < 500)) return m.v;
    const v = myWars();
    this.warsMemo = { key: key ?? '', me, at: now, v };
    return v;
  }

  /** The enemy seats of a war from my side, memoised per (war, side) on the seat tables' stamps (2026-09-25: the
   *  whole `structure` table was copied into a Map per war per FRAME). */
  private seats(w: GroundWarRow, side: 'a' | 'b'): WarSeat[] {
    const now = performance.now();
    const key = this.stampKey(SEAT_TABLES);
    const mk = `${w.warId}:${side}`;
    const m = this.seatMemo.get(mk);
    if (m && (key !== null ? m.key === key && now - m.at < 5000 : now - m.at < 500)) return m.v;
    const v = this.seatsNow(w, side);
    if (this.seatMemo.size > 32) this.seatMemo.clear(); // ended wars' entries
    this.seatMemo.set(mk, { key: key ?? '', at: now, v });
    return v;
  }

  /** The enemy seats of a war from my side: City Halls (or a city's centre while its hall is out of view), the guild hall. */
  private seatsNow(w: GroundWarRow, side: 'a' | 'b'): WarSeat[] {
    const kind = side === 'a' ? w.defenderKind : w.attackerKind;
    const id = side === 'a' ? w.defenderId : w.attackerId;
    // One primary-key find per seat instead of a copy of the whole table (2026-09-25).
    const byId = (atlas.conn?.db as unknown as { structure?: { structureId?: { find(id: bigint): StructureRow | undefined } } } | undefined)?.structure?.structureId;
    const structures = { get: (sid: bigint): StructureRow | undefined => { try { return byId?.find(sid) ?? undefined; } catch { return undefined; } } };
    const out: WarSeat[] = [];
    const seatOf = (s: StructureRow): void => {
      const fp = footprintOf(s.structureType);
      out.push({ x: s.posX, z: s.posZ, hx: fp.hx, hz: fp.hz, yaw: s.yaw });
    };
    const hallOf = (cityId: bigint): void => {
      const c = this.rows<CityRow>('city').find((x) => x.cityId === cityId);
      if (!c) return;
      const s = structures.get(c.cityHallStructureId);
      if (s) seatOf(s); else out.push({ x: c.centerX, z: c.centerZ, hx: 0, hz: 0, yaw: 0 });
    };
    if (kind === 'city') hallOf(id);
    else if (kind === 'civ') for (const m of this.rows<CivMemberRow>('playerCivMember')) { if (m.civId === id) hallOf(m.cityId); }
    else if (kind === 'guild') {
      const g = this.rows<GuildRow>('guild').find((x) => x.guildId === id);
      const s = g ? structures.get(g.hallStructureId) : undefined;
      if (s) seatOf(s);
    }
    return out;
  }


  // ── the per-frame side ──────────────────────────────────────────────────────
  private tick(): void {
    const inWorld = atlas.currentWorldId !== null && !atlas.spaceFlight && !!atlas.conn;
    const now = performance.now();
    if (!inWorld) { this.setChip(''); this.showPrompt(null); this.clearMarks(); return; }
    const wars = this.wars(now); // memoised on the member tables' stamps (2026-09-25)
    if (now - this.scanAt > 500) {
      this.scanAt = now;
      this.updateMarks(wars.length > 0);
    }
    this.positionMarks();
    if (wars.length === 0) { this.setChip(''); this.showPrompt(null); this.endPlanting(''); return; }
    const w = wars[0];
    const left = Math.max(0, ms(w.war.endsAt) - Date.now());
    const mine = w.side === 'a' ? w.war.attackerName : w.war.defenderName;
    const theirs = w.side === 'a' ? w.war.defenderName : w.war.attackerName;
    const [my, their] = w.side === 'a' ? [w.war.scoreA, w.war.scoreB] : [w.war.scoreB, w.war.scoreA];
    const planting = this.planting ? ` · planting the banner ${Math.min(BANNER_HOLD_S, Math.round((now - this.planting.startedMs) / 1000))}/${BANNER_HOLD_S} s` : '';
    this.setChip(`At war — ${mine} ${my} : ${their} ${theirs} · ${Math.floor(left / 3_600_000)} h left${wars.length > 1 ? ` · +${wars.length - 1} more` : ''}${planting}`);
    this.plantingTick(now);
    this.claimSeat(wars);
  }

  private claimSeat(wars: Array<{ war: GroundWarRow; side: 'a' | 'b' }>): void {
    const focus = interactFocus();
    let best: { id: string; warId: bigint; dist: number; label: string } | null = null;
    if (!this.planting && !atlas.seated && !atlas.hoverRide) {
      for (const { war, side } of wars) {
        const last = ms(side === 'a' ? war.lastBannerA : war.lastBannerB);
        if (Date.now() - last < BANNER_GAP_S * 1000) continue;
        for (const seat of this.seats(war, side)) {
          const d = seatDistance(atlas.localX, atlas.localZ, seat);
          if (d > BANNER_R_M) continue;
          const id = `war:seat:${war.warId}`;
          const label = interactPrompt(`plant ${side === 'a' ? war.attackerName : war.defenderName}'s banner (hold ${BANNER_HOLD_S} s)`);
          focus.claim({ id, dist: d, priority: 2, label });
          if (!best || d < best.dist) best = { id, warId: war.warId, dist: d, label };
        }
      }
    }
    const owned = best && focus.owns(best.id) ? best : null;
    this.focused = owned ? { id: owned.id, warId: owned.warId } : null;
    this.showPrompt(owned ? owned.label : null);
  }

  private key(): void {
    const f = this.focused;
    if (!f || !interactFocus().owns(f.id)) return;
    void this.plant(f.warId);
  }

  private async plant(warId: bigint): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    const ok = await act(conn.reducers.plantWarBanner({ warId }), 'The banner goes up — hold the ground');
    if (!ok) return;
    const war = this.rows<GroundWarRow>('groundWar').find((x) => x.warId === warId);
    const hexMe = this.me();
    const side = war && hexMe ? sideInWar(war, hexMe) : null;
    const seat = war && side ? this.seats(war, side).reduce<WarSeat | null>((b, st) => (!b || seatDistance(atlas.localX, atlas.localZ, st) < seatDistance(atlas.localX, atlas.localZ, b) ? st : b), null) : null;
    this.planting = { warId, x: atlas.localX, z: atlas.localZ, seat, startedMs: performance.now(), sending: false };
    this.showBanner(atlas.localX, atlas.localZ);
  }

  private plantingTick(now: number): void {
    const p = this.planting;
    if (!p) return;
    const away = p.seat ? seatDistance(atlas.localX, atlas.localZ, p.seat) : Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
    if (away > BANNER_R_M * 0.9) { this.endPlanting('You left the seat — the banner fell'); return; }
    if (p.sending || now - p.startedMs < (BANNER_HOLD_S + 1) * 1000) return;
    p.sending = true;
    void act(atlas.conn?.reducers.completeWarBanner({ warId: p.warId }), 'The banner stands — your side scores').then(() => this.endPlanting(''));
  }

  private endPlanting(msg: string): void {
    if (!this.planting) return;
    this.planting = null;
    if (msg) bus.emit('atlas:toast', { text: msg, kind: 'error', ms: 4000 });
    if (this.bannerMesh) { this.bannerMesh.removeFromParent(); this.bannerMesh = null; }
  }

  private showBanner(x: number, z: number): void {
    if (this.bannerMesh) this.bannerMesh.removeFromParent();
    const g = new THREE.Group();
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.06, 4.2, 8), new THREE.MeshStandardMaterial({ color: 0x5a4632, roughness: 0.8 }));
    pole.position.y = 2.1;
    const flag = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 0.9), new THREE.MeshStandardMaterial({ color: 0xb4462a, side: THREE.DoubleSide, roughness: 0.9 }));
    flag.position.set(0.72, 3.6, 0);
    g.add(pole, flag);
    g.position.set(x + 1.2, atlas.localY, z);
    this.scene.add(g);
    this.bannerMesh = g;
  }

  // ── the enemy markers ───────────────────────────────────────────────────────
  private markMaterial(): THREE.SpriteMaterial {
    if (this.markMat) return this.markMat;
    const c = document.createElement('canvas');
    c.width = 64; c.height = 64;
    const ctx = c.getContext('2d')!;
    ctx.fillStyle = '#d23a2a';
    ctx.beginPath(); ctx.moveTo(8, 12); ctx.lineTo(56, 12); ctx.lineTo(32, 50); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = '#1a0806'; ctx.lineWidth = 3; ctx.stroke();
    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.markMat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false, depthTest: false });
    markMrtOverlay(this.markMat);
    return this.markMat;
  }

  private updateMarks(atWar: boolean): void {
    if (!atWar) { this.clearMarks(); return; }
    const near: string[] = [];
    for (const [hex, r] of atlas.remotes) if (Math.hypot(r.targetX - atlas.localX, r.targetZ - atlas.localZ) <= MARK_RANGE_M) near.push(hex);
    const enemies = warTargets(near);
    for (const [hex, s] of this.marks) if (!enemies.has(hex)) { s.removeFromParent(); this.marks.delete(hex); }
    for (const hex of enemies) {
      if (this.marks.has(hex)) continue;
      const s = new THREE.Sprite(this.markMaterial());
      s.scale.set(0.55, 0.55, 1);
      s.renderOrder = 12;
      this.scene.add(s);
      this.marks.set(hex, s);
    }
  }

  private positionMarks(): void {
    for (const [hex, s] of this.marks) {
      const r = atlas.remotes.get(hex);
      if (!r) continue;
      const y = r.mesh ? r.mesh.position.y : atlas.localY;
      s.position.set(r.targetX, y + 2.55, r.targetZ);
    }
  }

  private clearMarks(): void {
    for (const s of this.marks.values()) s.removeFromParent();
    this.marks.clear();
  }

  private setChip(text: string): void {
    const c = this.chipEl;
    if (!c) return;
    if (!text) { if (c.style.display !== 'none') c.style.display = 'none'; return; }
    if (c.textContent !== text) c.textContent = text;
    if (c.style.display !== 'block') c.style.display = 'block';
  }
  private showPrompt(text: string | null): void {
    const p = this.prompt;
    if (!p) return;
    const show = text !== null;
    if ((p.style.display !== 'none') !== show) p.style.display = show ? 'block' : 'none';
    if (show && p.textContent !== text) p.textContent = text;
  }

  // ── the tab ────────────────────────────────────────────────────────────────
  // SOFT RENDER (2026-09-25): built into a scratch and patched over the live host (lib/soft_render.ts) — the war rows
  // no longer rebuild the tab under a stake being typed or a declaration being set up.
  private renderTab(real: HTMLElement): void { softRender(real, (h) => this.buildTab(h)); }
  private buildTab(host: HTMLElement): void {
    host.appendChild(head('Wars'));
    host.appendChild(muted('A war is declared by a side\'s leader on another city, civilisation, guild or group. It opens after a 30-minute warm-up and runs three days: members of the two sides may strike each other anywhere in the Commons (a downed enemy scores a point), and a banner planted at the enemy\'s seat — a City Hall or the guild hall — and held a minute uncontested scores five. Both leaders may make peace; a side may surrender; the stakes go to the side ahead at the end.'));
    const me = this.me();
    const wars = this.rows<GroundWarRow>('groundWar').filter((w) => w.state !== 'ended' || Date.now() - ms(w.endedAt) < 86_400_000);
    const mine = wars.filter((w) => me && sideInWar(w, me));
    const others = wars.filter((w) => !mine.includes(w));
    if (mine.length) { host.appendChild(head('Your wars')); for (const w of mine) host.appendChild(this.warCard(w)); }
    host.appendChild(this.declareCard());
    if (others.length) { host.appendChild(head('Elsewhere')); for (const w of others.slice(0, 12)) host.appendChild(this.warCard(w)); }
    if (!wars.length) host.appendChild(muted('No war is being fought.'));
  }

  private warCard(w: GroundWarRow): HTMLDivElement {
    const c = card();
    c.setAttribute('data-row', `war:${w.warId}`); // rows pair by identity in the soft render
    const me = this.me();
    const side = me ? sideInWar(w, me) : null;
    c.appendChild(el('div', 'font-weight:600', `${w.attackerName} (${KIND_LABEL[w.attackerKind] ?? w.attackerKind}) ⚔ ${w.defenderName} (${KIND_LABEL[w.defenderKind] ?? w.defenderKind})`));
    const row = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
    row.appendChild(chip(w.state === 'pending' ? `warm-up until ${new Date(ms(w.activeAt)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : w.state === 'active' ? `${Math.max(0, Math.floor((ms(w.endsAt) - Date.now()) / 3_600_000))} h left` : `ended — ${w.result}`, w.state === 'active' ? 'bad' : w.state === 'pending' ? 'warn' : ''));
    row.appendChild(chip(`${w.scoreA} : ${w.scoreB}`, 'info'));
    row.appendChild(chip(`stakes ${String(w.stakeA)} / ${String(w.stakeB)} ⓒ`));
    if (side) row.appendChild(chip(`you fight for ${side === 'a' ? w.attackerName : w.defenderName}`, 'good'));
    c.appendChild(row);
    const conn = atlas.conn;
    if (conn && w.state !== 'ended') {
      const actions = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      const leadA = this.leads(w.attackerKind, w.attackerId), leadB = this.leads(w.defenderKind, w.defenderId);
      if (leadB && w.state === 'pending') {
        const stake = input('stake', String(w.stakeA), 'number', '110px');
        stake.setAttribute('data-k', `answer-stake:${w.warId}`);
        actions.appendChild(stake);
        // The field ON SCREEN (2026-09-25, the soft render): a kept field holds the stake being typed.
        actions.appendChild(btn('Answer — match the stake', () => void act(conn.reducers.answerGroundWar({ warId: w.warId, stake: stakeOf((fieldByKey(this.tabEl, `answer-stake:${w.warId}`) ?? stake).value) }), 'Your side answers the declaration')));
      }
      if (leadA || leadB) {
        const asked = (leadA && w.peaceA) || (leadB && w.peaceB);
        actions.appendChild(btn(asked ? 'Peace proposed' : 'Propose peace', () => void act(conn.reducers.proposeGroundPeace({ warId: w.warId }), 'Peace proposed — the other leader must agree')));
        if (w.state === 'active') actions.appendChild(btn('Surrender', () => void act(conn.reducers.surrenderGroundWar({ warId: w.warId }), 'Your side surrenders')));
      }
      if (side && w.state === 'active') {
        const seats = this.seats(w, side);
        if (seats.length) actions.appendChild(btn('Waypoint to the enemy seat', () => void act(conn.reducers.createWaypoint({ name: `Seat: ${(side === 'a' ? w.defenderName : w.attackerName)}`.slice(0, 32), posX: seats[0].x, posZ: seats[0].z }), 'Waypoint set — plant your banner there (E)')));
      }
      if (actions.childElementCount) c.appendChild(actions);
    }
    const events = this.rows<EventRow>('groundWarEvent').filter((e) => e.warId === w.warId).sort((a, b) => ms(b.at) - ms(a.at)).slice(0, 5);
    for (const e of events) c.appendChild(muted(`${new Date(ms(e.at)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — ${e.text}`));
    return c;
  }

  private declareCard(): HTMLDivElement {
    const c = card();
    const mine = this.myEntities();
    // A FORM ISLAND (2026-09-25): its own listeners rewrite the target list as the player picks a side and a kind, so a
    // re-render must not rebuild it — kept whole while the sides the player leads are the same.
    c.setAttribute('data-soft-keep', `declare:${mine.map((e) => `${e.kind}:${e.id}`).join(',')}`);
    c.appendChild(el('div', 'font-weight:600', 'Declare a war'));
    if (mine.length === 0) {
      c.appendChild(muted('Only a side\'s leader may declare: a city\'s mayor, a civilisation\'s founding mayor, a guild\'s leader, a group\'s leader.'));
      return c;
    }
    const from = select(mine.map((e) => ({ value: `${e.kind}:${e.id}`, label: `${e.name} (${KIND_LABEL[e.kind]})` })));
    const kind = select(['city', 'civ', 'guild', 'group'].map((k) => ({ value: k, label: KIND_LABEL[k] })));
    const target = select([]);
    const stake = input('stake ⓒ', '0', 'number', '110px');
    const fee = muted('');
    const fill = (): void => {
      const [fk, fid] = from.value.split(':');
      const list = this.allEntities(kind.value).filter((e) => !(e.kind === fk && String(e.id) === fid));
      target.replaceChildren(...list.map((e) => { const o = document.createElement('option'); o.value = String(e.id); o.textContent = e.name; return o; }));
      fee.textContent = `The declaration costs ${WAR_FEE[fk] ?? 1000} ⓒ (a sink); your stake is held until the war ends.`;
    };
    from.addEventListener('change', fill);
    kind.addEventListener('change', fill);
    fill();
    c.appendChild(field('Your side', from));
    c.appendChild(field('On', kind, target));
    c.appendChild(field('Stake', stake));
    c.appendChild(fee);
    c.appendChild(btn('Declare war', () => {
      const [fk, fid] = from.value.split(':');
      if (!target.value) { bus.emit('atlas:toast', { text: 'Pick a side to declare on', kind: 'error', ms: 3000 }); return; }
      void act(atlas.conn?.reducers.declareGroundWar({
        attackerKind: fk, attackerId: BigInt(fid), defenderKind: kind.value, defenderId: BigInt(target.value),
        stake: stakeOf(stake.value),
      }), 'War declared — it opens after a 30-minute warm-up', () => this.rerender());
    }, true));
    return c;
  }

  dispose(): void {
    this.offInteract?.(); this.offInteract = null;
    this.clearMarks();
    this.chipEl?.remove();
    this.prompt?.remove();
  }
}

